import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, X, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Collections = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/collections/`);
      setCollections(response.data);
    } catch (error) {
      console.error('Error fetching collections:', error);
      toast.error('Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection?')) return;
    try {
      await axios.delete(`${API_URL}/api/collections/${collectionId}`);
      toast.success('Collection deleted successfully');
      fetchCollections();
    } catch (error) {
      toast.error('Failed to delete collection');
    }
  };

  const filteredCollections = collections.filter(collection =>
    collection.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Collections</h1>
            <p className="text-gray-500 mt-1">Group products to make your store easier to browse</p>
        </div>
        <Button onClick={() => navigate('/collections/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create collection
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search collections..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 max-w-md"
        />
      </div>

      {loading ? (
        <div className="text-center py-12">Loading collections...</div>
      ) : filteredCollections.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-500 mb-4">No collections found. Create your first collection to get started!</p>
            <Button onClick={() => navigate('/collections/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create collection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="divide-y">
            <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 text-sm font-medium text-gray-600">
              <div className="col-span-6">Title</div>
              <div className="col-span-2 text-center">Products</div>
              <div className="col-span-3">Product conditions</div>
              <div className="col-span-1"></div>
            </div>
            
            {filteredCollections.map((collection) => (
              <div key={collection.id} className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 items-center">
                <div 
                  className="col-span-6 cursor-pointer" 
                  onClick={() => navigate(`/collections/${collection.id}`)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{collection.title}</span>
                    {collection.collection_type === 'automated' && (
                      <Badge variant="secondary" className="text-xs">Smart</Badge>
                    )}
                  </div>
                  {collection.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-1">{collection.description}</p>
                  )}
                </div>
                <div className="col-span-2 text-center font-medium">{collection.listing_count || 0}</div>
                <div className="col-span-3 text-sm text-gray-600">
                  {collection.collection_type === 'manual' ? (
                    <span>Manual selection</span>
                  ) : (
                    <span>{collection.rules?.length || 0} condition(s)</span>
                  )}
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCollection(collection.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
    </DashboardLayout>
  );
};

export default Collections;
